import {useCallback, useMemo, useState} from 'react';
import moment from 'moment';
import {Checkbox} from '@mui/material';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {SVG} from '../../../../../../../../constants/assetLists';
import {
  NetworkDetailsFormValues,
  NetworkNodeFieldPath,
  RegulationFormValues,
} from '../../../../types';
import AppTextInput from '../../../../../../../../components/common/AppTextInput';
import RegulationRowMenu from './RegulationRowMenu';
import AppMUIAutocomplete from '../../../../../../../../components/common/AppMUIAutoComplete';
import AppFileInputButton from '../../../../../../../../components/common/AppFileInputButton';
import AppButton from '../../../../../../../../components/common/AppButton';
import RenderFiles from '../../../../../../../operations/components/Modals/AddAnnouncement/components/RenderFiles';
import {TIMING_TYPES} from '../../../../../../../operations/components/Modals/AddAnnouncement/data';
import {TIMES} from '../../../../../../../operations/components/Modals/AddAnnouncement/components/Time';
import CustomTiming from '../../../../../../../operations/components/Modals/CustomTiming';
import {DateInput} from '../../../../../../../operations/components/Modals/CustomTiming/components/DateInput';
import {ICONS} from '../../../../../../../users/data';
import {
  CustomTimingFormValues,
  useCustomTiming,
} from '../../../../../../../../context/CustomTimingContextProvider';
import {useOnBoardingContext} from '../../../../../../../../context/onBoarding/OnBoardingContextProvider';
import {RegulationCustomTimingSettings} from '../../../../types';
import {
  getEligibleRegulationAssigneeUsers,
  getUserDisplayName,
} from '../regulationAssigneeUtils';

const CHANGE_TYPE_TIME_OPTIONS = [
  {label: 'יום לפני', value: 'day_before'},
  {label: 'שבוע לפני', value: 'week_before'},
  {label: 'חודש לפני', value: 'month_before'},
];

const NOTIFICATION_TIMING_OPTIONS = [
  {label: 'יום לפני', value: '1_day_before'},
  {label: 'יומיים לפני', value: '2_days_before'},
  {label: '4 ימים לפני', value: '4_days_before'},
  {label: 'שבוע לפני', value: '1_week_before'},
];

const FILES_INPUT = [
  {
    id: 'media',
    name: 'files',
    accept: 'image/jpg, image/jpeg, image/png',
    icon: SVG.cameraIcon,
  },
  {
    id: 'documents',
    name: 'files',
    accept: 'application/pdf',
    icon: SVG.fileIcon,
  },
];

type Props = {
  selectedFieldPath: NetworkNodeFieldPath;
  globalIndex: number;
  displayIndex: number;
  selectedFolderId: string | null;
  onSaved?: () => void;
  onRemove: (globalIndex: number) => void;
};

function RegulationFormRow({
  selectedFieldPath,
  globalIndex,
  displayIndex,
  selectedFolderId,
  onSaved,
  onRemove,
}: Props) {
  const {control, setValue, getValues} =
    useFormContext<NetworkDetailsFormValues>();

  const {allRoleTypes} = useOnBoardingContext();

  const {clearCustomTimingData, setCustomTimingData} = useCustomTiming();

  const [showCustomTimingDialog, setShowCustomTimingDialog] = useState(false);

  const nodeDepartments =
    useWatch({control, name: `${selectedFieldPath}.departments`}) ?? [];
  const nodeRoles =
    useWatch({control, name: `${selectedFieldPath}.roles`}) ?? [];
  const nodeUsers =
    useWatch({control, name: `${selectedFieldPath}.users`}) ?? [];

  const rowBase = `${selectedFieldPath}.regulations.${globalIndex}`;

  // Per-row customTiming is stored on the form model, not just in context.
  const customTiming = useWatch({
    control,
    name: `${rowBase}.customTiming` as any,
  }) as RegulationCustomTimingSettings | null | undefined;

  const schedulingType = useWatch({
    control,
    name: `${rowBase}.schedulingType` as any,
  }) as string;

  const isAllDay = schedulingType === 'all_day';
  const isByDayAndTime = schedulingType === 'by_day_and_time';

  const sendNotification = useWatch({
    control,
    name: `${rowBase}.sendNotificationBeforeDate` as any,
  }) as boolean;

  const selectedDepartment = useWatch({
    control,
    name: `${rowBase}.department` as any,
  }) as string;

  const departmentOptions = useMemo(
    () =>
      nodeDepartments.map(dept => ({
        label: dept.name,
        value: dept.id,
        icon: ICONS?.find(icon => icon.id === dept.icon)?.icon,
      })),
    [nodeDepartments],
  );

  const managerUserOptions = useMemo(
    () =>
      getEligibleRegulationAssigneeUsers(
        nodeUsers,
        nodeRoles,
        allRoleTypes,
        selectedDepartment,
      ).map(user => ({
        label: getUserDisplayName(user),
        value: user.id,
      })),
    [nodeUsers, nodeRoles, allRoleTypes, selectedDepartment],
  );

  const isFieldVisible = useCallback(
    (fieldName: string) => {
      switch (fieldName) {
        case 'startDate':
          return [
            'default',
            'all_day',
            'by_day_and_time',
            'between_days_and_hours',
          ].includes(schedulingType ?? '');
        case 'endDate':
          return ['default', 'between_days_and_hours'].includes(
            schedulingType ?? '',
          );
        case 'startTime':
        case 'endTime':
          return [
            'by_day_and_time',
            'between_days_and_hours',
            'daily_at_fixed_time',
            'custom',
          ].includes(schedulingType ?? '');
        default:
          return true;
      }
    },
    [schedulingType],
  );

  const handleSchedulingTypeChange = useCallback(
    (newType: string) => {
      clearCustomTimingData();

      const base = `${selectedFieldPath}.regulations.${globalIndex}`;
      setValue(`${base}.schedulingType` as any, newType, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue(`${base}.startDate` as any, moment().format('YYYY-MM-DD'));
      setValue(`${base}.endDate` as any, moment().format('YYYY-MM-DD'));
      setValue(`${base}.startTime` as any, '');
      setValue(`${base}.endTime` as any, '');
      setValue(`${base}.time` as any, '');

      if (newType === 'all_day' || newType === 'by_day_and_time') {
        setValue(`${base}.endDate` as any, '');
      }

      if (newType === 'daily_at_fixed_time' || newType === 'custom') {
        setValue(`${base}.startDate` as any, '');
        setValue(`${base}.endDate` as any, '');
      }

      // Clear per-row customTiming unless switching to custom
      if (newType !== 'custom') {
        setValue(`${base}.customTiming` as any, null, {shouldDirty: true});
      }

      if (newType === 'custom') {
        setShowCustomTimingDialog(true);
      }
    },
    [selectedFieldPath, globalIndex, setValue, clearCustomTimingData],
  );

  const handleTimeModalChange = (value: boolean) => {
    clearCustomTimingData();
    setShowCustomTimingDialog(value);
  };

  const openCustomTimingModal = () => {
    // Hydrate context from this row's stored customTiming before opening
    const stored = getValues(`${rowBase}.customTiming` as any) as
      | RegulationCustomTimingSettings
      | null
      | undefined;
    if (stored) {
      setCustomTimingData(stored as CustomTimingFormValues);
    } else {
      clearCustomTimingData();
    }
    setShowCustomTimingDialog(true);
  };

  const formattedDate = (endDate: string | Date) =>
    new Date(endDate).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const customEndLabel = () => {
    switch (customTiming?.endCondition) {
      case 'never':
        return 'ממשיך ללא סיום';
      case 'end_date':
        return `מסתיים${formattedDate(customTiming?.endDate || '')}`;
      case 'occurrences':
        return `מסיים לאחר ${customTiming?.occurrences} הופעות`;
      default:
        return '';
    }
  };

  const handleTimeInput = useCallback(
    (field: 'startTime' | 'endTime', inputValue: string) => {
      const match = TIMES.find(t => t.title === inputValue);
      setValue(
        `${selectedFieldPath}.regulations.${globalIndex}.${field}` as any,
        match ? match.value : inputValue,
        {shouldValidate: true, shouldDirty: true},
      );
    },
    [selectedFieldPath, globalIndex, setValue],
  );

  const handleFileChange = useCallback(
    (newFiles: FileList | null) => {
      if (!newFiles) return;
      const path =
        `${selectedFieldPath}.regulations.${globalIndex}.files` as const;
      const existing = (getValues(path) as RegulationFormValues['files']) ?? [];
      setValue(path, [...existing, ...Array.from(newFiles)], {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [selectedFieldPath, globalIndex, getValues, setValue],
  );

  const handleFileRemove = useCallback(
    (fileIndex: number) => {
      const path =
        `${selectedFieldPath}.regulations.${globalIndex}.files` as const;
      const existing = (getValues(path) as RegulationFormValues['files']) ?? [];
      setValue(
        path,
        existing.filter((_, i) => i !== fileIndex),
        {shouldValidate: true, shouldDirty: true},
      );
    },
    [selectedFieldPath, globalIndex, getValues, setValue],
  );

  return (
    <>
      <div className="flex items-start gap-3 p-2.5 border border-gray70 rounded-lg shadow-sm">
        <span className="text-xl text-gray font-light self-center w-[20px]">
          {String(displayIndex).padStart(2, '0')}
        </span>

        <div className="flex-1 flex flex-col gap-3">
          <div className="flex gap-3">
            <Controller
              control={control}
              name={`${selectedFieldPath}.regulations.${globalIndex}.name`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppTextInput
                    label="שם"
                    placeholder="שם"
                    className="border-none"
                    required
                    {...field}
                    error={error?.message}
                  />
                </div>
              )}
            />

            <Controller
              control={control}
              name={`${selectedFieldPath}.regulations.${globalIndex}.description`}
              render={({field, fieldState: {error}}) => (
                <div className="flex-1">
                  <AppTextInput
                    label="תיאור קצר"
                    placeholder="תיאור קצר"
                    className="border-none"
                    {...field}
                    error={error?.message}
                  />
                </div>
              )}
            />
          </div>

          <div className="flex gap-3">
            <div className="flex-1 flex gap-3">
              <Controller
                control={control}
                name={`${selectedFieldPath}.regulations.${globalIndex}.department`}
                render={({field: {value, onChange}, fieldState: {error}}) => (
                  <div className="flex-1">
                    <AppMUIAutocomplete
                      label="מחלקה"
                      options={departmentOptions}
                      size="small"
                      fullWidth
                      getOptionLabel={option => option.label}
                      id={`regulation-department-${globalIndex}`}
                      renderValue={() => {
                        const selected = departmentOptions.find(
                          opt => opt.value === value,
                        );
                        return selected ? (
                          <div className="flex items-center">
                            <img
                              src={selected.icon}
                              alt=""
                              className="w-[18px] h-[18px] object-contain me-2"
                            />
                            {selected.label}
                          </div>
                        ) : null;
                      }}
                      renderOption={(props, option) => {
                        const {key, ...rest} = props;
                        return (
                          <li key={key} {...rest}>
                            <img
                              src={option.icon}
                              alt=""
                              className="w-[18px] h-[18px] object-contain me-2"
                            />
                            {option.label}
                          </li>
                        );
                      }}
                      value={
                        departmentOptions.find(opt => opt.value === value) ||
                        null
                      }
                      onChange={(_, newValue) => {
                        onChange(
                          (newValue as (typeof departmentOptions)[0] | null)
                            ?.value ?? '',
                        );
                        setValue(
                          `${selectedFieldPath}.regulations.${globalIndex}.assignee` as any,
                          '',
                        );
                      }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name={`${selectedFieldPath}.regulations.${globalIndex}.assignee`}
                render={({field: {value, onChange}, fieldState: {error}}) => (
                  <div className="flex-1">
                    <AppMUIAutocomplete
                      label="שיוך אישי"
                      options={managerUserOptions}
                      size="small"
                      fullWidth
                      getOptionLabel={option => option.label}
                      id={`regulation-assignee-${globalIndex}`}
                      renderOption={(props, option) => {
                        const {key, ...rest} = props;
                        return (
                          <li key={key} {...rest}>
                            {option.label}
                          </li>
                        );
                      }}
                      value={
                        managerUserOptions.find(opt => opt.value === value) ||
                        null
                      }
                      onChange={(_, newValue) =>
                        onChange(
                          (newValue as (typeof managerUserOptions)[0] | null)
                            ?.value ?? '',
                        )
                      }
                      error={!!error}
                      helperText={error?.message}
                    />
                  </div>
                )}
              />
            </div>

            <div className="flex-1 flex justify-between items-center gap-3">
              <div className="flex items-center flex-1 gap-2">
                  <div className="w-[65px] h-[56px] shrink-0">
                    <img
                      src={SVG.regulationBadgeIcon}
                      alt=""
                      className="w-full h-full object-contain"
                    />
                  </div>
                
                <span className="text-base text-black text-wrap">
                  קביעת זמן לשינוי סטטוס הרגולציה מצהוב לאדום
                </span>
              </div>

              <Controller
                control={control}
                name={`${selectedFieldPath}.regulations.${globalIndex}.changeTypeTime`}
                render={({field: {value, onChange}, fieldState: {error}}) => (
                  <div className="flex-1">
                    <AppMUIAutocomplete
                      label="קביעת זמן לשינוי סטטוס"
                      options={CHANGE_TYPE_TIME_OPTIONS}
                      size="small"
                      fullWidth
                      disableClearable
                      getOptionLabel={option => option.label}
                      id={`regulation-change-type-time-${globalIndex}`}
                      renderOption={(props, option) => {
                        const {key, ...rest} = props;
                        return (
                          <li key={key} {...rest}>
                            {option.label}
                          </li>
                        );
                      }}
                      value={
                        CHANGE_TYPE_TIME_OPTIONS.find(
                          opt => opt.value === value,
                        ) || CHANGE_TYPE_TIME_OPTIONS[0]
                      }
                      onChange={(_, newValue) =>
                        onChange(
                          (
                            newValue as
                              | (typeof CHANGE_TYPE_TIME_OPTIONS)[0]
                              | null
                          )?.value ?? '',
                        )
                      }
                      error={!!error}
                      helperText={error?.message}
                    />
                  </div>
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 place-items-start gap-3">
            <div className="w-full flex flex-col gap-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="flex items-center gap-2 flex-1">
                  <img
                    src={SVG.clockIcon}
                    alt=""
                    className="w-[20px] h-[20px] object-contain"
                  />
                  <Controller
                    control={control}
                    name={`${selectedFieldPath}.regulations.${globalIndex}.schedulingType`}
                    render={({field, fieldState: {error}}) => (
                      <AppMUIAutocomplete
                        label="תזמון מותאם"
                        size="small"
                        options={TIMING_TYPES}
                        sx={{flex: 1}}
                        getOptionLabel={option => option.title}
                        renderOption={(props, option) => {
                          const {key, ...rest} = props;
                          return (
                            <li key={key} {...rest}>
                              {option.title}
                            </li>
                          );
                        }}
                        value={
                          TIMING_TYPES.find(item => item.key === field.value) ||
                          null
                        }
                        onChange={(_, newValue) =>
                          handleSchedulingTypeChange(
                            (newValue as (typeof TIMING_TYPES)[0] | null)
                              ?.key ?? '',
                          )
                        }
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </div>

                {isFieldVisible('startDate') && (
                  <Controller
                    control={control}
                    name={`${selectedFieldPath}.regulations.${globalIndex}.startDate`}
                    render={({field, fieldState: {error}}) => (
                      <div className="flex-1">
                        <DateInput
                          dateFormat="EEEE, d 'ב'MMMM"
                          placeholderText="yyyy, mmm, dd"
                          selected={field.value ? new Date(field.value) : null}
                          onChange={date =>
                            field.onChange(date?.toISOString() || '')
                          }
                          label={
                            isAllDay ? 'יום' : isByDayAndTime ? 'ביום' : 'מ-'
                          }
                          error={error?.message}
                        />
                      </div>
                    )}
                  />
                )}
              </div>

              <div className="flex items-center gap-3 flex-1">
                {isFieldVisible('endDate') && (
                  <div className="flex items-center gap-2 flex-1">
                    {isFieldVisible('endDate') && (
                      <div className="w-3.5 h-3.5"></div>
                    )}

                    {isFieldVisible('endDate') && (
                      <Controller
                        control={control}
                        name={`${selectedFieldPath}.regulations.${globalIndex}.endDate`}
                        render={({field, fieldState: {error}}) => (
                          <div className={`flex-1`}>
                            <DateInput
                              dateFormat="EEEE, d 'ב'MMMM"
                              placeholderText="yyyy, mmm, dd"
                              selected={
                                field.value ? new Date(field.value) : null
                              }
                              onChange={date =>
                                field.onChange(date?.toISOString() || '')
                              }
                              label="עד-"
                              error={error?.message}
                            />
                          </div>
                        )}
                      />
                    )}
                  </div>
                )}

                <div className="flex items-center gap-3 flex-1">
                  <div className="flex items-center gap-2 flex-1">
                    {!isFieldVisible('endDate') &&
                      isFieldVisible('startTime') &&
                      isFieldVisible('endTime') && (
                        <div className="w-3.5 h-3.5"></div>
                      )}

                    {isFieldVisible('startTime') && (
                      <Controller
                        control={control}
                        name={`${selectedFieldPath}.regulations.${globalIndex}.startTime`}
                        render={({field, fieldState: {error}}) => (
                          <div className={`flex-1`}>
                            <AppMUIAutocomplete
                              freeSolo
                              disableClearable
                              options={TIMES.map(t => t.title)}
                              label="משעה"
                              size="small"
                              value={field.value ?? ''}
                              onInputChange={(_e, newVal, reason) => {
                                if (reason === 'input')
                                  handleTimeInput('startTime', newVal);
                              }}
                              onChange={(_, newVal) => {
                                if (typeof newVal === 'string')
                                  handleTimeInput('startTime', newVal);
                              }}
                              error={!!error}
                              helperText={error?.message}
                            />
                          </div>
                        )}
                      />
                    )}
                  </div>

                  {isFieldVisible('endTime') && (
                    <Controller
                      control={control}
                      name={`${selectedFieldPath}.regulations.${globalIndex}.endTime`}
                      render={({field, fieldState: {error}}) => (
                        <div className={`flex-1`}>
                          <AppMUIAutocomplete
                            freeSolo
                            disableClearable
                            options={TIMES.map(t => t.title)}
                            label="לשעה"
                            size="small"
                            value={field.value ?? ''}
                            onInputChange={(_e, newVal, reason) => {
                              if (reason === 'input')
                                handleTimeInput('endTime', newVal);
                            }}
                            onChange={(_, newVal) => {
                              if (typeof newVal === 'string')
                                handleTimeInput('endTime', newVal);
                            }}
                            error={!!error}
                            helperText={error?.message}
                          />
                        </div>
                      )}
                    />
                  )}
                </div>
              </div>

              {schedulingType === 'custom' && (
                <div className="flex items-center gap-2 flex-1">
                  <div className="w-3.5 h-3.5"></div>
                  <div className="flex flex-col gap-1">
                    {customTiming?.repeatEvery && (
                      <span className="text-base font-medium">
                        חוזר על עצמו כל {customTiming.repeatEvery}{' '}
                        {customTiming?.repeatType === 'weekly'
                          ? 'שבועות'
                          : 'חודשים'}
                      </span>
                    )}

                    {customTiming?.endCondition && (
                      <span className="text-base font-medium">
                        {customEndLabel()}
                      </span>
                    )}
                    <AppButton
                      onClick={openCustomTimingModal}
                      style={{paddingInline: 15, paddingBlock: 6}}
                      variant="custom"
                      className="bg-white border border-[#CAC4D0] text-[#007AFF] text-[14px] font-medium rounded-[8px] max-w-fit">
                      לעדכון פרטים
                    </AppButton>
                  </div>
                </div>
              )}
            </div>

            <div className="w-full flex flex-1 items-center justify-between gap-3">
              <div className="flex items-center gap-2 flex-1">
                <div className="flex items-center w-[65px] shrink-0">
                  <Controller
                    control={control}
                    name={`${selectedFieldPath}.regulations.${globalIndex}.sendNotificationBeforeDate`}
                    render={({field}) => (
                      <Checkbox
                        size="small"
                        color="secondary"
                        checked={!!field.value}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                  <img
                    src={SVG.bellBlackIcon}
                    alt=""
                    className="w-5 h-5 object-contain"
                  />
                </div>

                <span className="text-base text-black text-wrap">
                  שליחת התראה לפני מועד ביצוע
                </span>
              </div>

              {sendNotification && (
                <Controller
                  control={control}
                  name={`${selectedFieldPath}.regulations.${globalIndex}.notificationTiming`}
                  render={({field: {value, onChange}, fieldState: {error}}) => (
                    <div className="flex-1">
                      <AppMUIAutocomplete
                        label="תזמון התראה"
                        options={NOTIFICATION_TIMING_OPTIONS}
                        size="small"
                        fullWidth
                        getOptionLabel={option => option.label}
                        id={`regulation-notification-timing-${globalIndex}`}
                        renderOption={(props, option) => {
                          const {key, ...rest} = props;
                          return (
                            <li key={key} {...rest}>
                              {option.label}
                            </li>
                          );
                        }}
                        value={
                          NOTIFICATION_TIMING_OPTIONS.find(
                            opt => opt.value === value,
                          ) || null
                        }
                        onChange={(_, newValue) =>
                          onChange(
                            (
                              newValue as
                                | (typeof NOTIFICATION_TIMING_OPTIONS)[0]
                                | null
                            )?.value ?? '',
                          )
                        }
                        error={!!error}
                        helperText={error?.message}
                      />
                    </div>
                  )}
                />
              )}
            </div>
          </div>

          <Controller
            control={control}
            name={`${selectedFieldPath}.regulations.${globalIndex}.files`}
            render={({field: {value}}) => (
              <div className="flex flex-col gap-2">
                <p className="text-base font-bold text-[#49454F]">קבצים</p>

                <div className="flex">
                  <div className="flex items-center gap-2 pb-2 border-b border-b-gray70">
                    {FILES_INPUT?.map(item => (
                      <AppFileInputButton
                        id={`file-input-${globalIndex}-${item.id}`}
                        icon={item.icon}
                        accept={item.accept}
                        multiple
                        onChange={e => handleFileChange(e.target.files)}
                      />
                    ))}

                    <p className="text-base text-textColor">
                      יש להעלות קבצים בפורמט PNG, JPG, PDF בלבד. 25MB גודל
                      מקסימלי
                    </p>
                  </div>
                </div>

                {Array.isArray(value) && value.length > 0 && (
                  <div className="flex flex-col gap-2">
                    {(value as RegulationFormValues['files']).map(
                      (file, idx) => (
                        <RenderFiles
                          key={idx}
                          file={file}
                          index={idx}
                          handleFileRemove={() => handleFileRemove(idx)}
                        />
                      ),
                    )}
                  </div>
                )}
              </div>
            )}
          />
        </div>

        <div className="flex items-center gap-2">
          <RegulationRowMenu
            selectedFieldPath={selectedFieldPath}
            globalIndex={globalIndex}
            selectedFolderId={selectedFolderId}
            onSaved={onSaved}
          />

          <button
            type="button"
            className="w-8 h-8 border border-gray rounded-lg flex items-center justify-center shadow-sm"
            onClick={() => onRemove(globalIndex)}>
            <img
              src={SVG.deleteGrayIcon}
              alt="מחק"
              className="w-4 h-[18px] object-contain"
            />
          </button>
        </div>
      </div>

      <CustomTiming
        open={showCustomTimingDialog}
        onClose={() => handleTimeModalChange(false)}
        onCancel={() => {
          handleTimeModalChange(false);
          handleSchedulingTypeChange('default');
        }}
        onSubmit={data => {
          setValue(`${rowBase}.customTiming` as any, data, {
            shouldDirty: true,
          });
          handleTimeModalChange(false);
        }}
      />
    </>
  );
}

export default RegulationFormRow;
